'use client';

import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import { PRODUCT_CONFIG } from '@eliadmin/core/src/constants/product-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import React from 'react';

type AvailabilityLabelProps = {
  isAvailable: boolean;
  className?: string;
  customName?: string;
  customIcon?: React.ReactNode;
  quantity?: number;
  customColor?: string | null;
  customColorText?: string | null;
  tooltipText?: string;
};

const createBackgroundColor = (hexColor: string | null, magnitude: number): string | undefined => {
  if (!hexColor || !magnitude) {
    return undefined;
  }
  // Remove the '#' if it's present
  hexColor = hexColor.replace('#', '');

  if (hexColor.length === 6 || hexColor.length === 8) {
    const hasAlpha = hexColor.length === 8;
    const decimalColor = parseInt(hexColor.substring(0, 6), 16); // Parse the RGB part of the hex

    let r = (decimalColor >> 16) + magnitude;
    r = Math.min(255, Math.max(0, r)); // Ensure r is between 0 and 255

    let g = ((decimalColor >> 8) & 0x00ff) + magnitude;
    g = Math.min(255, Math.max(0, g)); // Ensure g is between 0 and 255

    let b = (decimalColor & 0x0000ff) + magnitude;
    b = Math.min(255, Math.max(0, b)); // Ensure b is between 0 and 255

    const newHex = ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0'); // Convert to hex and pad

    if (hasAlpha) {
      const alpha = hexColor.substring(6, 8); // Extract the alpha part (last 2 characters)
      return `#${newHex}${alpha}`; // Return the new color with alpha included
    } else {
      return `#${newHex}`; // Return the new color without alpha
    }
  } else {
    return `#${hexColor}`; // Return the original hex if it's not a valid 6/8-length hex string
  }
};

export function AvailabilityLabel({
  isAvailable,
  className,
  customIcon,
  customName,
  customColor,
  customColorText,
  tooltipText,
  quantity,
}: AvailabilityLabelProps) {
  const { t } = useTranslations();
  const availabilityName = customName ?? (isAvailable ? 'text238' : 'text496');
  const availabilityIcon =
    customIcon ??
    (isAvailable
      ? PRODUCT_CONFIG.availabilityLabel.availableIcon
      : PRODUCT_CONFIG.availabilityLabel.unavailableIcon);

  const [showTooltip, setShowTooltip] = React.useState(false);

  return (
    <Badge
      size="md"
      style={{
        color: customColorText || undefined,
        backgroundColor: customColor
          ? customColor
          : customColorText
            ? createBackgroundColor(customColorText, 175)
            : undefined,
      }}
      className={cn(
        'flex flex-grow-0 gap-1 rounded-md font-semibold text-destructive-foreground',
        'ea-availability-label',
        {
          ['ea-availability-label--available bg-transparent text-success-foreground']: isAvailable,
          ['ea-availability-label--unavailable bg-transparent text-destructive-foreground']:
            !isAvailable,
        },
        className,
      )}
      onMouseEnter={tooltipText ? () => setShowTooltip(true) : undefined}
      onMouseLeave={tooltipText ? () => setShowTooltip(false) : undefined}
    >
      {t(availabilityName)}
      <span className="-ml-[5px] whitespace-nowrap font-semibold">
        {isAvailable && quantity ? (quantity > 5 ? '> 5 ks' : `${quantity} ks`) : ''}
      </span>
      {tooltipText && showTooltip && (
        <span
          className={cn(
            'ea-availability-label__tooltip absolute bottom-full left-[80%] z-10 ml-1 whitespace-nowrap rounded-[4px] border border-border bg-white p-[5px] text-sm font-semibold text-black shadow',
          )}
          style={{
            transform: 'translate(-50%, 10px)',
            animation: 'tooltipFadeIn 0.3s ease forwards',
            pointerEvents: 'none', // Prevents the tooltip from blocking mouse events
          }}
        >
          <HtmlRenderer html={tooltipText} />
        </span>
      )}
    </Badge>
  );
}
